import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useTour } from '../../contexts/TourContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowRight,
  BookOpen,
  Rocket,
} from 'lucide-react';

interface NavigationMapping {
  old: string;
  new: string;
  location: string;
  notes?: string;
}

const CUSTOMER_NAV_MAPPINGS: NavigationMapping[] = [
  { old: 'Home / Dashboard', new: 'Dashboard', location: 'Left sidebar', notes: 'Click charts to drill down' },
  { old: 'Purchasing > PO Overview', new: 'Purchasing', location: 'Left sidebar', notes: 'Use tabs for different views' },
  { old: 'Purchasing > PO Lines', new: 'Purchasing > PO Lines tab', location: 'Left sidebar' },
  { old: 'Purchasing > Forecast', new: 'Purchasing > Forecast tab', location: 'Left sidebar' },
  { old: 'Purchasing > ASN', new: 'Purchasing > ASN tab', location: 'Left sidebar' },
  { old: 'Purchasing > Deliveries', new: 'Purchasing > Deliveries tab', location: 'Left sidebar' },
  { old: 'Quality > NCRs', new: 'Quality', location: 'Left sidebar', notes: 'Use tabs for NCRs/Concessions' },
  { old: 'Suppliers List', new: 'Suppliers', location: 'Left sidebar' },
  { old: 'Supplier Profile', new: 'Suppliers > Click supplier', location: 'Detail page' },
  { old: 'Reports / Export', new: 'Each page has export', location: 'Page actions', notes: 'Look for Export button' },
  { old: 'User Settings', new: 'Settings', location: 'Left sidebar (bottom)' },
  { old: 'Admin Panel', new: 'Admin Setup', location: 'Left sidebar (bottom)' },
];

const SUPPLIER_NAV_MAPPINGS: NavigationMapping[] = [
  { old: 'Home / Dashboard', new: 'Dashboard', location: 'Left sidebar' },
  { old: 'My POs', new: 'Purchase Orders', location: 'Left sidebar' },
  { old: 'Create ASN', new: 'Purchase Orders > ASN tab', location: 'Left sidebar' },
  { old: 'Deliveries', new: 'Purchase Orders > Deliveries', location: 'Left sidebar' },
  { old: 'Quality Issues', new: 'Quality', location: 'Left sidebar' },
  { old: 'Company Profile', new: 'Company Profile', location: 'Left sidebar' },
  { old: 'Onboarding Form', new: 'My Onboarding', location: 'Left sidebar' },
  { old: 'Documents', new: 'Documents', location: 'Left sidebar' },
];

export function QuickReferenceModal() {
  const { showQuickReference, setShowQuickReference, startTour } = useTour();
  const { user } = useAuth();

  const isSupplier = user?.platform === 'SUPPLIER';
  const mappings = isSupplier ? SUPPLIER_NAV_MAPPINGS : CUSTOMER_NAV_MAPPINGS;

  const handleStartTour = () => {
    setShowQuickReference(false);
    startTour();
  };

  return (
    <Dialog open={showQuickReference} onOpenChange={setShowQuickReference}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BookOpen className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle>Quick Reference Guide</DialogTitle>
              <DialogDescription>
                Old portal navigation → New Supplier Portal navigation
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 border-b">
                  <th className="text-left py-2 px-3 font-medium">Old Portal</th>
                  <th className="text-center py-2 px-3 font-medium w-8"></th>
                  <th className="text-left py-2 px-3 font-medium">Supplier Portal</th>
                  <th className="text-left py-2 px-3 font-medium">Location</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping, idx) => (
                  <tr key={idx} className="border-b last:border-b-0 hover:bg-slate-50">
                    <td className="py-2 px-3 text-muted-foreground">{mapping.old}</td>
                    <td className="py-2 px-3 text-center">
                      <ArrowRight className="h-4 w-4 text-blue-500 inline" />
                    </td>
                    <td className="py-2 px-3 font-medium">{mapping.new}</td>
                    <td className="py-2 px-3">
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        {mapping.location}
                      </span>
                      {mapping.notes && (
                        <p className="text-xs text-muted-foreground mt-1">{mapping.notes}</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 space-y-3">
            <h4 className="font-medium text-sm">Other Key Changes</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded-lg border">
                <h5 className="font-medium text-sm">Tabs Inside Pages</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  Purchasing, Quality, and Suppliers pages now have tabs to organize related views.
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border">
                <h5 className="font-medium text-sm">Click-to-Drill-Down</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  Dashboard charts are interactive. Click on data points to see detailed breakdowns.
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border">
                <h5 className="font-medium text-sm">View Selector</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  Use the dropdown in the top bar to switch between Site, Company, and Group views.
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border">
                <h5 className="font-medium text-sm">Notifications</h5>
                <p className="text-xs text-muted-foreground mt-1">
                  Bell icon in top bar shows all notifications. Click to view and manage alerts.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => setShowQuickReference(false)}>
            Close
          </Button>
          <Button onClick={handleStartTour}>
            <Rocket className="h-4 w-4 mr-2" />
            Take Interactive Tour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
